// noinspection JSUnusedGlobalSymbols

/**
 * Dropdown component module.
 * @module shared/components/Dropdown
 *
 */
import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Select, {components} from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import AsyncCreatableSelect from 'react-select/async-creatable';
import debug from 'debug';
import styled, {withTheme} from 'styled-components/macro';
import {fill, layoutAttribute} from './cssUtils';
// import Icon from './Icon';
import {TitleLabel} from '../index';

const log = debug('circleblack:dropdown');

const _Wrapper = styled.div`
  max-width: ${props => props.maxWidth || '100%'};
  margin: 5px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  ${props => fill(props.width || '180px')};
  ${layoutAttribute.css};
`;

const _OptionLabel = styled.div`
  font-family: ${props => props.theme.bodyText.name};
  font-size: ${props => props.theme.bodyText.size};
  color: ${props => `rgba(${[props.theme.dark ? '255,255,255' : '0,0,0', props.disabled ? 0.5 : 0.9].join(',')})`};
  text-align: left;
`;

const _OptionSubLabel = styled.div`
  font-family: ${props => props.theme.labelText.name};
  font-size: ${props => props.theme.labelText.size};
  color: ${props => `rgba(${[props.theme.dark ? '255,255,255' : '0,0,0', props.disabled ? 0.5 : 0.7].join(',')})`};
  text-align: left;
  margin-top: 5px;
`;

const _GroupLabel = styled.div`
  font-family: ${props => props.theme.labelText.name};
  font-size: ${props => props.theme.labelText.size};
  color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  text-align: left;
`;

const _GroupDivider = styled.div`
  background-color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'};
  height: 1px;
  width: 100%;
  margin-top: 2px;
`;

/**
 * Dropdown component based on 'react-select' to create dropdown menu
 * @name Dropdown
 */
const Dropdown = (props) => {
  const {
    title,
    placeholder,
    dataPoint,
    defaultValue,
    options,
    onChange,
    isClearable, // later TODO default to false? seems to default to true inside react-select
    isCreatable,
    isLoading,
    isMulti,
    isSearchable = true,
    width,
    maxWidth,
    layout,
    disabled,
    readOnly,
    required,
    validation,
    type = 'dropdown',
    dataFunction,
    help,
  } = props;

  let SelectType = Select;
  if (dataFunction && !isCreatable) {
    SelectType = AsyncSelect;
  } else if (!dataFunction && isCreatable) {
    SelectType = CreatableSelect;
  } else if (dataFunction && isCreatable) {
    SelectType = AsyncCreatableSelect;
  }

  function callOnChange(newValue) {
    log('selected', dataPoint.name, newValue);
    onChange(dataPoint.name, disabled ? undefined : newValue);
  }

  const selectRef = useRef(null);

  const [border, setBorder] = useState('rgba(255,65,65,0)');

  const validate = (value) => {
    const {custom} = validation || {};
    let isValid = true;
    if (custom) {
      isValid = custom(value);
    }
    setBorder(isValid ? 'rgba(255,65,65,0)' : 'rgba(255,65,65,1)');
    return isValid;
  };

  const callOnInputChange = newValue => {
    validate(newValue);
  };

  const styling = {
    placeholder: styles => ({
      ...styles,
      fontFamily: props.theme.bodyText.name,
      fontSize: props.theme.bodyText.size,
      color: props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      textAlign: 'left',
      margin: props.readOnly ? '2px 0' : '2px',
    }),
    input: styles => ({
      ...styles,
      fontFamily: props.theme.bodyText.name,
      fontSize: props.theme.bodyText.size,
      color: props.theme.dark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
      textAlign: 'left',
    }),
    singleValue: styles => ({
      ...styles,
      fontFamily: props.theme.bodyText.name,
      fontSize: props.theme.bodyText.size,
      color: props.theme.dark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
      textAlign: 'left',
    }),
    loadingIndicator: styles => ({
      ...styles,
      color: props.theme.dark ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 0, 0, 1)',
    }),
    loadingMessage: styles => ({
      ...styles,
      fontFamily: props.theme.bodyText.name,
      fontSize: props.theme.bodyText.size,
      color: props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      textAlign: 'center',
    }),
    multiValue: styles => ({
      ...styles,
      display: 'flex',
      padding: '0px 12px',
      whiteSpace: 'nowrap',
      alignItems: 'center',
      border: '1px solid transparent',
      borderRadius: '4px',
      background: 'none',
      borderColor: '#00D0BE',
    }),
    multiValueLabel: styles => ({
      ...styles,
      fontFamily: 'ProximaLight, sans-serif',
      fontSize: '14px',
      paddingLeft: '0px',
      color: '#00D0BE',
    }),
    // ToDo: Icon color not working for some reason. Commented it out and using this built in MultiValueRemove for now.
    multiValueRemove: (styles, state) => {
      if (disabled || readOnly) {
        return {display: 'none'};
      }

      const bgColor = () => {
        if (state.isFocused) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
        }
        return 'transparent';
      };
      return {
        ...styles,
        color: props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
        ':hover': {backgroundColor: props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'},
        backgroundColor: bgColor(),
      };
    },
    noOptionsMessage: styles => ({
      ...styles,
      fontFamily: props.theme.bodyText.name,
      fontSize: props.theme.bodyText.size,
      color: props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      textAlign: 'center',
    }),
    clearIndicator: styles => ({
      ...styles,
      padding: '0px',
      color: props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      ':hover': {
        color: props.theme.dark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
      },
    }),
    indicatorSeparator: styles => ({
      ...styles,
      backgroundColor: props.theme.dark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      margin: '0px 5px',
      display: readOnly ? 'none' : '',
    }),
    dropdownIndicator: styles => ({
      ...styles,
      padding: '0px',
      color: props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      ':hover': {
        color: props.theme.dark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
      },
      display: readOnly ? 'none' : '',
    }),
    indicatorsContainer: styles => ({
      ...styles,
      color: props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      ':hover': {
        color: props.theme.dark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
      },
    }),
    control: styles => {
      delete styles.minHeight;

      const additionalStyling = {};

      if (props.theme.neumorphic) {
        additionalStyling.backgroundImage = 'linear-gradient(to bottom right, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))';
        additionalStyling.boxShadow = '-1px -1px 0 0 rgba(255, 255, 255, 0.1), 1px 1px 0 0 rgba(0, 0, 0, 0.4)';
        additionalStyling.backgroundColor = props.theme.tileBackgroundColor;
      }

      return ({
        ...styles,
        backgroundColor: `rgba(${[props.theme.dark ? '255,255,255' : '0,0,0', readOnly ? 0 : 0.1].join(',')})`,
        borderRadius: '4px',
        padding: props.readOnly ? '2px 0' : '2px',
        boxSizing: 'border-box',
        borderWidth: props.readOnly ? '3px 0' : '3px',
        borderColor: border,
        ':hover': {borderColor: border},
        ':active': {borderColor: border},
        boxShadow: border,
        cursor: 'pointer',
        ...additionalStyling,
      });
    },
    valueContainer: styles => ({
      ...styles,
      padding: '0px',
    }),
    option: (styles, state) => {
      delete styles[':active'];
      const additionalStyling = {};

      if (props.theme.neumorphic) {
        additionalStyling.borderRadius = '2px';
        additionalStyling.width = 'fill';
        additionalStyling.height = 'fill';
        additionalStyling.margin = '0 3px 0 3px';
        if (state.isSelected) {
          additionalStyling.boxShadow = 'inset -1px -1px 0 0 rgba(255, 255, 255, 0.18), inset 1px 1px 0 0 rgba(0, 0, 0, 0.3)';
          additionalStyling.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }
        if (state.isFocused) {
          additionalStyling.backgroundColor = props.theme.dark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)';
        }
      }

      const bgColor = () => {
        if (state.isSelected) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
        }
        if (state.isFocused) {
          return props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
        }
        return 'transparent';
      };
      return {
        ...styles,
        padding: '10px',
        backgroundColor: bgColor(),
        cursor: 'pointer',
        ...additionalStyling,
      };
    },
    groupHeading: styles => ({
      ...styles,
      padding: '10px 5px 5px 5px',
      textTransform: 'none',
    }),
    menu: styles => {
      delete styles.marginTop;
      delete styles.marginBottom;
      return {
        ...styles,
        backgroundColor: props.theme.tileBackgroundColor,
        borderRadius: '4px',
        borderStyle: 'none',
        boxShadow: props.theme.neumorphic ? '-1px -1px 0 0 rgba(255, 255, 255, 0.1), 1px 1px 0 0 rgba(0, 0, 0, 0.4), 1px 2px 6px 0 rgba(0, 0, 0, 0.5)' : '0px 1px 4px rgba(0,0,0,0.5)',
        'margin-top': props.theme.neumorphic ? '2px' : 0,
        marginBottom: '5px',
      };
    },
  };

  const Option = prop => (
    <components.Option {...prop}>
      <_OptionLabel disabled={prop.isDisabled}>{prop.data.label}</_OptionLabel>
      {prop.data.subLabel && <_OptionSubLabel disabled={prop.isDisabled}>{prop.data.subLabel}</_OptionSubLabel>}
    </components.Option>
  );

  const GroupHeading = prop => (
    <components.GroupHeading {...prop}>
      <_GroupLabel>{prop.children}</_GroupLabel>
      <_GroupDivider/>
    </components.GroupHeading>
  );

  // ToDo: Icon color not working for some reason. Commenting out and using built in MultiValueRemove for now.
  // const MultiValueRemove = prop => {
  //   console.log('MultiValueRemove click prop', prop);
  //   return !(disabled || readOnly)
  //     ? <Icon width={'14px'} svg={'remove'} margin={'0 0 0 6px'} color={'#00D0BE'} action={() => {prop.innerProps.onClick();}}/>
  //     : null;
  // };

  return (
    <_Wrapper disabled={disabled} width={width} maxWidth={maxWidth} layout={layout}>
      {title != null && <TitleLabel title={title} help={help}/>}
      <div style={{cursor: disabled ? 'not-allowed' : 'default'}}>
        <SelectType loadOptions={dataFunction}
                    isClearable={isClearable}
                    isMulti={isMulti}
                    isSearchable={isSearchable || type === 'typeahead'}
                    isLoading={isLoading}
                    placeholder={readOnly ? '-' : placeholder}
                    options={options}
                    value={dataPoint.value}
                    defaultValue={defaultValue}
                    defaultOptions={true}
                    styles={{
                      ...styling,
                      menuPortal: base => ({...base, zIndex: 999}),
                    }}
                    menuPortalTarget={document.body}
                    isDisabled={disabled || readOnly}
                    onChange={callOnChange}
                    onInputChange={callOnInputChange}
                    components={{Option, GroupHeading}}
                    required={required}
                    ref={selectRef}
        />
        {required && <input
          tabIndex={-1}
          autoComplete="off"
          style={{
            opacity: 0,
            width: width,
            height: 0,
            position: 'absolute',
          }}
          value={dataPoint.value}
          onChange={() => {}}
          onFocus={() => selectRef.current.focus()}
          required={required}
        />}
      </div>
    </_Wrapper>
  );
};

Dropdown.buildSimpleOption = function buildSimpleOption(option) {
  return {label: option, value: option};
};

export default withTheme(Dropdown);

Dropdown.propTypes = {
  /**
   * Title of the Dropdown, to use for it's displayed header. (optional)
   */
  title: PropTypes.string,
  /**
   * Placeholder text
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Dropdown Input Data. Should be an object with name and value properties. Value should be an object with label and value properties.
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.shape({
      label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      value: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object),
      ]).isRequired,
    }),
  }).isRequired,
  /**
   * Value that should be selected by default (optional). Should be an object with label and value as properties.
   */
  defaultValue: PropTypes.shape({
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    value: PropTypes.isRequired,
  }),
  /**
   * Set of options. Should be an array of objects, each object with label, subLabel (optional), and value properties.
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    subLabel: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    value: PropTypes.isRequired,
    isDisabled: PropTypes.bool,
  })).isRequired,
  /**
   * On Change function. Function should accept two arguments, dataPoint.name and newValue.
   */
  onChange: PropTypes.func,
  /**
   * Whether this component is Clearable. (optional)
   */
  isClearable: PropTypes.bool,
  /**
   * Whether this component is Creatable. (optional)
   */
  isCreatable: PropTypes.bool,
  /**
   * Will cause the select to be displayed in the loading state, even if not currently waiting for loadOptions to resolve
   */
  isLoading: PropTypes.bool,
  /**
   * Whether this component will use multiselect.
   */
  isMulti: PropTypes.bool,
  /**
   * Whether this component is Searchable. (optional)
   */
  isSearchable: PropTypes.bool,
  /**
   * Width of the Dropdown Component, as CSS (e.g. '500px', '50%')
   */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * Max Width of the Dropdown Component, as CSS (e.g. '500px', '50%')
   */
  maxWidth: PropTypes.string,
  /**
   * Whether this component is disabled. (optional)
   */
  disabled: PropTypes.bool,
  /**
   * Whether this component is read only
   */
  readOnly: PropTypes.bool,
  /**
   /**
   * Whether this component is required. (optional)
   */
  required: PropTypes.bool,
  /**
   * Validation object containing a 'custom' property. Custom should be a function that accepts one argument and returns a boolean. (optional)
   */
  validation: PropTypes.shape({
    custom: PropTypes.func,
  }),
  /**
   * The type of dropdown: dropdown or typeahead. (optional)
   */
  type: PropTypes.string,
  /**
   * Function that returns a promise, which is the set of options to be used once the promise resolves. (optional)
   */
  dataFunction: PropTypes.func,
  /**
   * Help object to pass to Label
   */
  help: PropTypes.shape({
    /**
     * Help icon description
     */
    description: PropTypes.string,
    /**
     * Help type
     */
    type: PropTypes.oneOf(['action', 'modal', 'tooltip']).isRequired,
    /**
     * If help type is modal, the modal size. Defaults to sm
     */
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    /**
     * If help type is modal, the modal title
     */
    title: PropTypes.string,
    /**
     * If help type is modal, the modal body
     */
    content: PropTypes.node,
    /**
     * If help type is action, the action to perform
     */
    action: PropTypes.func,
    /**
     * If button needs to be customized, then pass the text, action, and data for button
     */
    button: PropTypes.shape({
      text: PropTypes.string,
      action: PropTypes.func,
      data: PropTypes.object,
    }),
  }),
  /**
   * The theme prop provided by the ThemeProvider.
   */
  theme: PropTypes.object,
  layout: layoutAttribute.propTypes,
};
